import { BaseExportModal, ExportModalProps } from './ExportModal'
import {
  buildDealExportColumns,
  normalizeSelectedColumnIds,
} from './dealExportColumns'
import { useMemo } from 'react'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useDownload } from 'features/download-widget'
import {
  getDownloadStatus,
  startDealsReportExportV2,
  startDealsExportV2,
} from 'services/downloadsApi'

import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'

interface DealExportModalProps extends ExportModalProps {
  kind: 'deals_table' | 'deals_report'
}

export default function DealExportModal(props: DealExportModalProps) {
  const { enableEmailIntegration } = useFeatureFlagService()
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const normalizedSelectedColumns = useMemo(
    () => normalizeSelectedColumnIds(selectedColumns),
    [selectedColumns]
  )
  const { startDownload } = useDownload()

  const columns = useMemo(
    () => buildDealExportColumns({ enableEmailIntegration }),
    [enableEmailIntegration]
  )

  const exportApi =
    props.kind === 'deals_report'
      ? startDealsReportExportV2
      : startDealsExportV2

  const onSubmit = async (
    selectedCols: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await startDownload({
      items: [{ id: 0, name: 'Exporting Deals...' }],
      initiate: async () => {
        const { sort, ...filterWithoutSort } = (props.filters ?? {}) as Record<
          string,
          unknown
        >
        const response = await exportApi({
          selected_columns: selectedCols,
          export_type: exportType,
          filter_params: {
            ...filterWithoutSort,
            product: props.productSelected,
            include_ids: props.selectedIds ?? [],
            exclude_ids: props.excludeIds ?? [],
          },
          sort: sort as string | undefined,
        })
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getDownloadStatus,
      label: 'Deals',
    })
  }

  return (
    <BaseExportModal
      title="Select Columns for Deals Download"
      subTitle="Download up to 10,000 deals at a time, with any of the following columns. Any filters already applied will remain active."
      submitText="Export Deals"
      columns={columns}
      alwaysSelectedColumns={['sku']}
      selectedColumns={normalizedSelectedColumns}
      onSubmit={onSubmit}
      show={props.show}
      handleClose={props._handleClose}
    />
  )
}
